import { create } from 'zustand';
import tradingService from '../services/tradingService';

const useMarketStore = create((set, get) => ({
  // State
  marketOrders: [],
  limitOrders: [],
  isLoading: false,
  isPlacingOrder: false,
  error: null,
  successMessage: null,

  // Actions
  placeMarketOrder: async (orderData) => {
    set({ isPlacingOrder: true, error: null, successMessage: null });
    
    try {
      const payload = {
        id: 0,
        state: 0,
        pair: orderData.pair,
        operation: orderData.operation, // 1 for buy, 0 for sell
        type: 0, 
        quantity: parseFloat(orderData.quantity),
        price: parseFloat(orderData.price) || 0,
        executed: true,
        quantity_left: parseFloat(orderData.quantity),
        vwap: 0,
       
      };

      const response = await tradingService.placeMarketOrder(payload);
      
      // Add to market orders
      const newOrder = { ...payload, id: response.id || Date.now() };
      set(state => ({
        marketOrders: [newOrder, ...state.marketOrders],
        successMessage: `Order placed successfully!`,
        isPlacingOrder: false
      }));

      // Clear success message after 5 seconds
      setTimeout(() => {
        set({ successMessage: null });
      }, 5000);

      return response;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to place market order', 
        isPlacingOrder: false 
      });
      throw error;
    }
  },

  placeLimitOrder: async (orderData) => {
    set({ isPlacingOrder: true, error: null, successMessage: null });
    
    try {
      const payload = {
        pair: orderData.pair,
        operation: orderData.operation, 
        type: 0, 
        quantity: parseFloat(orderData.quantity),
        price: parseFloat(orderData.price),
      };

      const response = await tradingService.placeLimitOrder(payload);
      
      // Add to limit orders
      const newOrder = { 
        ...payload, 
        id: response.id || Date.now(),
        state: 0,
        executed: false,
        quantity_left: parseFloat(orderData.quantity),
        vwap: 0
      };
      
      set(state => ({
        limitOrders: [newOrder, ...state.limitOrders],
        successMessage: `order placed successfully!`,
        isPlacingOrder: false
      }));

      // Clear success message after 5 seconds
      setTimeout(() => {
        set({ successMessage: null });
      }, 5000);

      return response;
    } catch (error) {
      // Handle nested error structure from API
      let errorMessage = 'Failed to place limit order';
      
      if (error.message) {
        // Check if error.message is an object with nested structure
        if (typeof error.message === 'object' && error.message.message) {
          errorMessage = error.message.message;
        } else if (typeof error.message === 'string') {
          errorMessage = error.message;
        }
      }
      
      set({ 
        error: errorMessage, 
        isPlacingOrder: false 
      });
      // Don't throw the error since we're handling it in the store
      // The component will show the toast based on the error state
    }
  },

  fetchLimitOrders: async (params = {}) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await tradingService.getLimitOrders(params);
      const orders = response?.results || response || [];
      
      set({ 
        limitOrders: orders, 
        isLoading: false 
      });
      
      return response;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to fetch limit orders', 
        limitOrders: [],
        isLoading: false 
      });
      return { results: [], count: 0, next: null, previous: null };
    }
  },

  cancelLimitOrder: async (orderId) => {
    set({ isLoading: true, error: null });
    
    try {
      await tradingService.cancelOrderJWT(orderId);
      
      // Remove from limit orders
      set(state => ({
        limitOrders: state.limitOrders.filter(order => order.id !== orderId),
        isLoading: false
      }));
      
      return true;
    } catch (error) {
      set({ 
        error: error.message || 'Failed to cancel order', 
        isLoading: false 
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  clearSuccessMessage: () => {
    set({ successMessage: null });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  // Utility functions
  getOrderById: (orderId) => {
    const { limitOrders, marketOrders } = get();
    return [...limitOrders, ...marketOrders].find(order => order.id === orderId);
  },

  getOrdersByPair: (pair) => {
    const { limitOrders, marketOrders } = get();
    return [...limitOrders, ...marketOrders].filter(order => order.pair === pair);
  },

  getBuyOrders: () => {
    const { limitOrders, marketOrders } = get();
    return [...limitOrders, ...marketOrders].filter(order => order.type === 0);
  },

  getSellOrders: () => {
    const { limitOrders, marketOrders } = get();
    return [...limitOrders, ...marketOrders].filter(order => order.type === 1);
  }
}));

export default useMarketStore;
